import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Row,
  Select,
  Typography,
} from 'antd';
import { get, identity, startCase } from 'lodash';
import queryString from 'query-string';

import { OverlayLoading } from 'components/overlay-loading';
import PdButton from 'components/pd-button';
import { DisplayImage } from 'components/displayImage';

import InspectionService from 'services/inspection';
import ReviewInspectionService from 'services/reviewInspection';
import { INSPECTION_STATUS } from './constant';

import { errHandling } from 'utils';
import {
  priceInputNumberFormatter,
  priceInputNumberParser,
} from 'utils/inputNumber.js';

import './style.scss';

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const FIELDS = {
  ASK_BY: 'ask_by',
  DAMAGE_PENALTY_ID: 'damage_penalty_id',
  IMAGE: 'images',
  NOTES: 'notes',
  PRICE: 'price',
};

export function InspectionCreateEdit({
  closeModal = identity,
  inspection = {},
}) {
  const [form] = Form.useForm();
  const [inspectionImgSrc, setInspectionImgSrc] = useState('');
  const [listDamagePenalty, setListDamagePenalty] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [showFullInspectionImg, setShowInspectionImg] = useState(false);
  const [value, setValue] = useState({});

  const location = useLocation();
  const { status: pageStatus } = queryString.parse(location.search) || {};

  const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 16, offset: 2 },
    labelAlign: 'left',
  };

  // if other damage, enable price field & show save button
  const isOtherDamage =
    get(value, ['damage_penalty', 'title'], '').toLowerCase() === 'other';

  async function handleSubmit(values) {
    setLoadingButton(true);
    try {
      const penaltyId = value.id;
      const payload = {
        penalty: {
          price: values.price,
        },
      };
      await ReviewInspectionService.updatePenalty(penaltyId, payload);
      message.success('Successfully edit the inspection');
      closeModal();
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }
  async function fetchDamagePenaltyList() {
    setLoadingPage(true);
    try {
      const {
        data: { data },
      } = await InspectionService.getDamagePenaltyList({
        product_id: inspection.product_id,
      });
      setListDamagePenalty(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    const { damage_penalty: { id } = {} } = inspection;
    setValue({ ...inspection, damage_penalty_id: id });
    form.setFieldsValue({ ...inspection, damage_penalty_id: id });
    fetchDamagePenaltyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOnClickInspectionImg(imgSrc) {
    setInspectionImgSrc(imgSrc);
    setShowInspectionImg(true);
  }

  function handleOnCloseInspectionImg() {
    setShowInspectionImg(false);
    setInspectionImgSrc('');
  }

  return (
    <Content>
      <DisplayImage
        imageSrc={inspectionImgSrc}
        onClose={handleOnCloseInspectionImg}
        show={showFullInspectionImg}
      />
      {loadingPage && <OverlayLoading open={loadingPage} />}
      <Form
        form={form}
        className="pd-checking-inspection-form"
        onFinish={handleSubmit}
        initialValues={value}>
        <div className="section-container">
          <Row className="pd-margin-bottom-lg" type="flex" justify="center">
            <Title level={4}>Detail Inspection Result</Title>
          </Row>
          <div className="inner-section">
            <Form.Item label="Ask by" name={FIELDS.ASK_BY} {...formItemLayout}>
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="Damage"
              name={FIELDS.DAMAGE_PENALTY_ID}
              {...formItemLayout}>
              <Select disabled>
                {listDamagePenalty.map(({ id, title }) => (
                  <Option key={id} value={id}>
                    {startCase(title)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Notes" name={FIELDS.NOTES} {...formItemLayout}>
              <TextArea disabled />
            </Form.Item>

            <Form.Item label="Upload Image" {...formItemLayout}>
              {get(value, 'images', []).map((imgSrc, idx) => (
                <img
                  key={idx}
                  height={100}
                  onClick={handleOnClickInspectionImg.bind(this, imgSrc)}
                  src={imgSrc}
                  width={100}
                  alt="checking-inspection"
                />
              ))}
            </Form.Item>

            <Form.Item
              label="Price"
              name={FIELDS.PRICE}
              rules={[
                {
                  message: 'Please input the price!',
                  required: true,
                },
              ]}
              {...formItemLayout}>
              <InputNumber
                className="w-full"
                formatter={priceInputNumberFormatter}
                parser={priceInputNumberParser}
                disabled={
                  pageStatus === INSPECTION_STATUS.DONE || !isOtherDamage
                }
              />
            </Form.Item>
          </div>
        </div>

        {pageStatus === INSPECTION_STATUS.CHECK && isOtherDamage && (
          <div className="button-container">
            <Form.Item>
              <Row type="flex" justify="center">
                <PdButton
                  className="w-half height-32"
                  full
                  htmlType="submit"
                  loading={loadingButton}
                  type="primary">
                  Save
                </PdButton>
              </Row>
            </Form.Item>
          </div>
        )}
      </Form>
    </Content>
  );
}

export default InspectionCreateEdit;
